import { useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';

import { SEARCH_CREW_MEMBER_BY_TEXT } from 'gql';

import { CREW_MEMBER_SEARCH_TEXT } from './text';

const groupCrewMembersByStatus = (crewMembers) => {
	if (!crewMembers?.length) return [];

	const active = [];
	const inactive = [];
	const groups = [];

	crewMembers.forEach(({ id, fullName, isActive, driverCard }) => {
		const option = {
			title: fullName,
			value: id,
			driverCard: driverCard ?? '',
			status: isActive ? 'active' : 'inactive',
		};

		if (isActive) active.push(option);
		else inactive.push(option);
	});

	if (active.length) {
		groups.push({
			group: CREW_MEMBER_SEARCH_TEXT.activeDriversGroup(active.length),
			options: active,
		});
	}

	if (inactive.length) {
		groups.push({
			group: CREW_MEMBER_SEARCH_TEXT.inactiveDriversGroup(inactive.length),
			options: inactive,
		});
	}

	return groups;
};

export const useCrewMemberSearch = ({ setValue, setDriverName }) => {
	const [crewMembers, setCrewMembers] = useState([]);
	const [getCrewMember, { loading: isSearching }] = useLazyQuery(SEARCH_CREW_MEMBER_BY_TEXT);

	const updateCrewMembers = useCallback((crewMembers) => {
		setCrewMembers(groupCrewMembersByStatus(crewMembers));
	}, []);

	const onChange = useCallback(
		(e) => {
			const value = e.target.value;

			setDriverName(value);

			const isEmpty = !value.trim().length;

			if (isEmpty) {
				setValue('');
				updateCrewMembers([]);
			}
		},
		[setDriverName, setValue, updateCrewMembers]
	);

	const onDebounce = useCallback(
		async (text) => {
			const normalizedText = text?.trim();

			if (!normalizedText) {
				updateCrewMembers([]);
				return;
			}

			try {
				const { data } = await getCrewMember({ variables: { text: normalizedText } });

				updateCrewMembers(data?.crewMemberSearch?.results);
			} catch {
				updateCrewMembers([]);
			}
		},
		[getCrewMember, updateCrewMembers]
	);

	const onSelect = useCallback(
		(options) => {
			const selected = options?.[0];

			if (!selected) return;

			const isInactive = selected.status === 'inactive';

			if (isInactive) {
				window.open(`/crew-management/${selected.value}`, '_blank', 'noopener,noreferrer');
				updateCrewMembers([]);
				return;
			}

			setDriverName(selected.title);
			setValue(selected.value);
			updateCrewMembers([]);
		},
		[setDriverName, setValue, updateCrewMembers]
	);

	return { isSearching, crewMembers, onDebounce, onSelect, onChange };
};
